import gql from 'graphql-tag';

// ============================================================================
// Types
// ============================================================================

export interface ActivityPoolEntry {
  id: string;
  accessPointId: string;
  dropPointId: string;
  locationName: string | null;
  postalCode: string | null;
  city: string | null;
  upsApSince: string | null;
  action: 'PAUSED' | 'REACTIVATED';
  triggeredBy: 'APP' | 'EMPLOYEE';
  employeeId: string | null;
  employeeName: string | null;
  deactivatedUntil: string | null;
  comment: string | null;
  createdAt: string;
}

export interface ActivityPoolResponse {
  entries: ActivityPoolEntry[];
  totalCount: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface ReactivationPoolEntry {
  id: string;
  accessPointId: string;
  dropPointId: string;
  locationName: string | null;
  postalCode: string | null;
  city: string | null;
  upsApSince: string | null;
  triggeredBy: 'APP' | 'EMPLOYEE';
  employeeId: string | null;
  employeeName: string | null;
  pausedSince: string;
  deactivatedUntil: string | null;
  comment: string | null;
}

export interface ReactivationPoolResponse {
  entries: ReactivationPoolEntry[];
  totalCount: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export type InternalActivityPoolQuery = {
  internalActivityPool: ActivityPoolResponse;
};

export type InternalReactivationPoolQuery = {
  internalReactivationPool: ReactivationPoolResponse;
};

export type ActivitySortField = 'pausedSince' | 'reactivatedSince' | 'createdAt';

// ============================================================================
// Access Point Overview Types
// ============================================================================

export type AccessPointDisplayStatus = 'ACTIVE' | 'INACTIVE' | 'CANCELLED';

export type AccessPointOverviewSortField = 'deactivatedUntil' | 'lastDeactivationTimestamp' | 'locationName';

export interface AccessPointOverviewEntry {
  id: string;
  accessPointID: string;
  dropPointId: string | null;
  locationName: string | null;
  postalCode: string | null;
  city: string | null;
  isActive: boolean;
  displayStatus: AccessPointDisplayStatus;
  deactivatedUntil: string | null;
  lastDeactivationTimestamp: string | null;
  userEmail: string | null;
}

export interface AccessPointOverviewResponse {
  entries: AccessPointOverviewEntry[];
  totalCount: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export type InternalAccessPointOverviewQuery = {
  internalAccessPointOverview: AccessPointOverviewResponse;
};

// ============================================================================
// Queries
// ============================================================================

export const GET_ACTIVITY_POOL = gql`
  query InternalActivityPool(
    $limit: Int
    $offset: Int
    $sortBy: String
    $sortOrder: String
    $triggeredBy: String
    $pausedSinceFrom: String
    $pausedSinceTo: String
    $reactivatedSinceFrom: String
    $reactivatedSinceTo: String
  ) {
    internalActivityPool(
      limit: $limit
      offset: $offset
      sortBy: $sortBy
      sortOrder: $sortOrder
      triggeredBy: $triggeredBy
      pausedSinceFrom: $pausedSinceFrom
      pausedSinceTo: $pausedSinceTo
      reactivatedSinceFrom: $reactivatedSinceFrom
      reactivatedSinceTo: $reactivatedSinceTo
    ) {
      entries {
        id
        accessPointId
        dropPointId
        locationName
        postalCode
        city
        upsApSince
        action
        triggeredBy
        employeeId
        employeeName
        deactivatedUntil
        comment
        createdAt
      }
      totalCount
      limit
      offset
      hasMore
    }
  }
`;

export const GET_REACTIVATION_POOL = gql`
  query InternalReactivationPool(
    $limit: Int
    $offset: Int
    $sortBy: String
    $sortOrder: String
    $triggeredBy: String
    $pausedSinceFrom: String
    $pausedSinceTo: String
  ) {
    internalReactivationPool(
      limit: $limit
      offset: $offset
      sortBy: $sortBy
      sortOrder: $sortOrder
      triggeredBy: $triggeredBy
      pausedSinceFrom: $pausedSinceFrom
      pausedSinceTo: $pausedSinceTo
    ) {
      entries {
        id
        accessPointId
        dropPointId
        locationName
        postalCode
        city
        upsApSince
        triggeredBy
        employeeId
        employeeName
        pausedSince
        deactivatedUntil
        comment
      }
      totalCount
      limit
      offset
      hasMore
    }
  }
`;

// ============================================================================
// Access Point Overview Queries & Mutations
// ============================================================================

export const GET_ACCESS_POINT_OVERVIEW = gql`
  query InternalAccessPointOverview(
    $limit: Int
    $offset: Int
    $sortBy: String
    $sortOrder: String
    $displayStatus: String
  ) {
    internalAccessPointOverview(
      limit: $limit
      offset: $offset
      sortBy: $sortBy
      sortOrder: $sortOrder
      displayStatus: $displayStatus
    ) {
      entries {
        id
        accessPointID
        dropPointId
        locationName
        postalCode
        city
        isActive
        displayStatus
        deactivatedUntil
        lastDeactivationTimestamp
        userEmail
      }
      totalCount
      limit
      offset
      hasMore
    }
  }
`;

export const INTERNAL_SILENT_TOGGLE_AP_STATUS = gql`
  mutation INTERNAL_SILENT_TOGGLE_AP_STATUS(
    $stationId: String!
    $active: Boolean!
    $internalUserId: String!
    $comment: String!
  ) {
    internalSilentToggleDropPointStatus(
      stationId: $stationId
      active: $active
      internalUserId: $internalUserId
      comment: $comment
    ) {
      id
    }
  }
`;

export const INTERNAL_EXTEND_BLACKOUT_SCHEDULE = gql`
  mutation INTERNAL_EXTEND_BLACKOUT_SCHEDULE(
    $accessPointID: String!
    $internalUserId: String!
    $comment: String!
  ) {
    internalExtendBlackoutSchedule(
      accessPointID: $accessPointID
      internalUserId: $internalUserId
      comment: $comment
    ) {
      id
    }
  }
`;
